import { FormControlLabel, FormControlLabelProps, Radio, useTheme } from "@mui/material";

import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";

type FormRadioProps = Omit<FormControlLabelProps, "control">;

export const FormRadio = ({ sx, ...rest }: FormRadioProps) => {
  const theme = useTheme();

  if (!theme.palette.forms || !theme.palette.formsComponents?.radio) {
    return <InvalidThemeErrorMessage />;
  }

  const styles = mergeSx(
    {
      display: "inline-flex",
      margin: 0, // Override default MUI style
      userSelect: "none",
      ["& .MuiRadio-root"]: {
        // Make the radio button invisible, but still focusable
        position: "absolute",
        opacity: 0,
        pointerEvents: "none",
      },
      ["&:has(.Mui-focusVisible) .MuiFormControlLabel-label"]: {
        color: theme.palette.formsComponents.radio.selectedTextColor,
        backgroundColor: theme.palette.formsComponents.radio.selectedBackgroundColor,
        borderColor: theme.palette.formsComponents.radio.selectedBackgroundColor,
      },
      ["&:has(.Mui-checked) .MuiFormControlLabel-label"]: {
        color: theme.palette.formsComponents.radio.selectedTextColor,
        backgroundColor: theme.palette.formsComponents.radio.selectedBackgroundColor,
        borderColor: theme.palette.formsComponents.radio.selectedBackgroundColor,
        ["&.Mui-disabled"]: {
          color: theme.palette.formsComponents.radio.selectedTextColor,
        },
      },
      ["&:not(:has(.Mui-checked)) .MuiFormControlLabel-label:not(.Mui-disabled):hover"]: {
        color: theme.palette.formsComponents.radio.textColor,
        backgroundColor: theme.palette.formsComponents.radio.hoverBackgroundColor,
        borderColor: theme.palette.formsComponents.radio.borderColor,
      },
      ["& .MuiFormControlLabel-label"]: {
        flexGrow: 1,
        minWidth: 0,
        margin: 0,
        padding: { xs: theme.spacing(1.25, 2), sm: theme.spacing(1.125, 2) },
        color: theme.palette.formsComponents.radio.textColor,
        ...theme.typography.h6,
        textAlign: "center",
        backgroundColor: theme.palette.formsComponents.radio.backgroundColor,
        border: `1px solid ${theme.palette.formsComponents.radio.borderColor}`,
        borderRadius: theme.radius(1),
        outlineColor: "transparent",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
        boxSizing: "border-box",
        transition:
          "background-color 200ms ease-out, color 200ms ease-out, border-color 200ms ease-out, outline 100ms ease-out",
        ["&.Mui-disabled"]: {
          opacity: 0.4,
          color: theme.palette.formsComponents.radio.textColor,
        },
      },
    },
    sx
  );

  return <FormControlLabel {...rest} control={<Radio disableRipple />} sx={styles} />;
};
